import { Plugin } from './Plugin';
import { Entity, EntityDependency } from '../objects';

import { Object3D } from 'three';

export class EntityPlugin extends Plugin{
	constructor(manager){
		super(manager);

		this.entities = new Set();

		this.products 	= new Map();
		this.objects 	= new Map();

		this.entityDependencies = new Map();

		this.floorTileId = 25;
		this.wallTileId = 28;
	}

	get loaded(){
		return this.entities.size == this.objects.size;
	}

	attach(){
		customization.on('product-added', this.onProductAdded);
		customization.on('product-modified', this.onProductModified);
		customization.on('product-removed', this.onProductRemoved);
	}

	detach(){
		customization.off('product-added', this.onProductAdded);
		customization.off('product-modified', this.onProductModified);
		customization.off('product-removed', this.onProductRemoved);
	}

	onProductAdded = (code) => {
		this.setProduct(code);
	}

	onProductRemoved = (code) => {
		this.removeEntity(this.objects.get(code));
	}

	onProductModified = (code) => {
		this.setProduct(code);
	}

	removeProduct(code){
		if(this.products.has(code)){
			const product = this.products.get(code);
			this.products.delete(code);

			switch(product.entity.entity_type_id){
				case this.floorTileId:
				case this.wallTileId:
					return;
					break;
			}

			const object = this.objects.get(code);
			this.remove(object);
		}
	}

	removeEntity(entity){
		if(!entity) return;

		const code = entity.product.entity.code;
		const entityTypeId = entity.product.entity.entity_type_id; 
		switch(entityTypeId){
			case this.floorTileId:
			case this.wallTileId:
				return;
				break;
		}
		this.products.delete(code);
		this.remove(entity);
	}

	registerEntity(code, entity){
		this.objects.set(code, entity);
	}

	unregisterEntity(code){
		const object = this.objects.get(code);
		this.objects.delete(code);
		[...this.entityDependencies].filter(([dependencyCode, entityCode]) => entityCode == code).forEach(([dependencyCode, entityCode]) => this.entityDependencies.delete(dependencyCode));
	}

	setProduct(code){
		const {product, isUser} = customization.products.get(code);
		if(product){
			this.products.set(code, product);

			switch(product.entity.entity_type_id){
				case this.floorTileId:
				case this.wallTileId:
					return;
			}

			if(this.entityDependencies.has(code)){
				const entityTypeCode = this.entityDependencies.get(code);
				const entity = this.objects.get(entityTypeCode);
				const prd 	 = this.products.get(entityTypeCode);
				entity.isUser = isUser;
				this.createDependencies(entity, prd);
				entity.layout();
				this.viewer.requestRender();
				this.emit('entity-updated', entity);
			}
			else this.createEntity(code, product, isUser);
		}

	}

	createEntity(code, product, isUser = false){
		const pivot = customization.scene.entitiesIds.find(e => e.entity_id == product.entity.id);
		if(!pivot) return console.warn(`Not a scene entity ${product.code}`);


		if(this.objects.has(code) && this.objects.get(code).product.code == product.code){
			const entity = this.objects.get(code);
			entity.isUser = true;
			entity.layout();
			// console.warn(`Entity ${product.code} already in scene`)
			return;
		}

		if(product.model){

			const entity = new Entity(this.viewer, product.model.filename, pivot, product, isUser);
			this.createDependencies(entity, product);

			product.entity.dependencies_types.forEach(type => {
				this.entityDependencies.set(type.code, code);
			});

			const previous = this.objects.get(code);

			if(previous && !previous.loaded){
				previous.removeEventListener('loaded', previous.postCreate2)
			}
			this.objects.set(code, entity);

			entity.postCreate = () => {
				this.removeEntity(previous);
			}

			entity.postCreate2 = () => {
		        this.add(entity);
				entity.postCreate = null;
				entity.postCreate2 = null;
			}

			if(entity.loaded) postCreate();
			else{
				entity.addEventListener('loaded', entity.postCreate);
				entity.addEventListener('loaded', entity.postCreate2);
			}
		}
		else console.warn(`The product [${product.code}] has no 3D model associated`);
	}

	createDependencies(entity, product){
		const dependencies = [];
		for(let dependencyData of product.entity.dependenciesIds){
			const dependency = product.entity.dependencies.find(d => d.id == dependencyData.dependency_id);

			const dep = this.createDependency(entity, dependency, dependencyData);
			if(dep){
				dependencies.push(dep);
			}
		}
		entity.addDependencies(dependencies);
	}

	createDependency(entity, dependency, data){
		const prd = customization.products.get(dependency.entity_type.code);

		if(prd){
			const { product } = prd;
			if(product && product.entity.code == dependency.code && !entity.has(product.id)){
				if(product.model){
			        return new EntityDependency(this.viewer, product.model.filename, data, product);
			    }
				else console.warn(`The product [${product.code}] has no 3D model associated`);
			}
		}
	}

	add(entity){
		if(!this.entities.has(entity)){
			this.entities.add(entity);
			this.viewer.add(entity);

			this.viewer.requestRender();

			this.emit('entity-added', entity);
		}
	}

	remove(entity){
		if(this.entities.has(entity)){
			this.viewer.remove(entity);
			this.entities.delete(entity);

			this.viewer.requestRender();

			this.emit('entity-removed', entity);
			entity.freeMemory();
		}
	}

}